import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Activity, AutomataService, Automation } from '@vendasta/automata';
import { AccountGroup, AccountGroupService } from '@galaxy/account-group';
import { User } from '@vendasta/iam';
import { SubscriptionList } from '@vendasta/rx-utils';
import { OrderInterface } from '@vendasta/sales-orders';
import { LoadRequest, TableDataService } from '@vendasta/va-filter2-table';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { EntityTypeService } from '../../shared/entities';
import { SalesOrdersService } from '../../data-services/sales-orders.service';
import { UsersService } from '../../data-services/users.service';
import { CrmObjectInterface } from '@vendasta/crm/lib/_internal/interfaces/crm-objects.interface';

export interface ActivityRow {
  activity: Activity;
  accountGroup: AccountGroup;
  order: OrderInterface;
  user: User;
  contact: CrmObjectInterface;
  company: CrmObjectInterface;
}

@Injectable({ providedIn: 'root' })
export class CurrentlyWaitingTableService implements OnDestroy, TableDataService<ActivityRow> {
  private readonly subscriptions = SubscriptionList.new();
  private readonly totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();
  private cursors: { [id: number]: string } = { 0: '' };

  private automation: Automation;
  private workflowStepId: string;

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly partnerId$: Observable<string>,
    private readonly automataService: AutomataService,
    private accountGroupService: AccountGroupService,
    private salesOrderService: SalesOrdersService,
    private usersService: UsersService,
    private entityTypesService: EntityTypeService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  initialize(automation: Automation, workflowstepId: string): void {
    this.automation = automation;
    this.workflowStepId = workflowstepId;
  }

  load(request: LoadRequest): Observable<ActivityRow[]> {
    if (request.pageIndex === 0) {
      this.cursors = { 0: '' };
    }
    const nextCursor = this.cursors[request.pageIndex] || '';
    return this.listActivities(nextCursor, request.pageSize, request.pageIndex);
  }

  listActivities(nextCursor: string, pageSize: number, pageIndex: number): Observable<ActivityRow[]> {
    const listResponse$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.automataService.listCurrentlyWaitingActivities(
          partnerId,
          null,
          this.automation.id,
          this.workflowStepId,
          nextCursor,
          pageSize,
        );
      }),
      take(1),
      shareReplay(1),
    );

    listResponse$.subscribe((resp) => {
      this.cursors[pageIndex + 1] = resp.nextCursor;
      this.totalResults$$.next(resp.totalResults);
    });

    const activities$: Observable<Activity[]> = listResponse$.pipe(
      map((resp) => {
        if (!resp || !resp.results) {
          return [];
        }
        return resp?.results;
      }),
    );

    const entities = this.entityTypesService.getEntityObservablesFromActivitiesBasedOnType(
      this.partnerId$,
      activities$,
      this.automation.entityType,
    );

    return combineLatest([
      activities$,
      entities.accountGroups$,
      entities.orders$,
      entities.users$,
      entities.contacts$,
      entities.companies$,
    ]).pipe(
      map(([activities, accountGroups, orders, users, contacts, companies]) => {
        return activities.map((a, i) => {
          return {
            activity: a,
            accountGroup: accountGroups[i] || null,
            order: orders[i] || null,
            user: users[i] || null,
            contact: contacts[i] || null,
            company: companies[i] || null,
          };
        });
      }),
    );
  }
}
